import { watch, onMounted, ref, reactive, computed } from 'vue';
import { userStore } from '@/store/user-store';
import { IComment } from '@/models/comment.model';
import { IUser } from '@/models/user.model';
import { RepositoryFactory } from '@/api/repositoryFactory';
import { IThought } from '@/models/thought.model';
import useConverter from '../general/useConverter';

const CommentRepository = RepositoryFactory.get('comment');
const { toBool } = useConverter();

interface CommentLists {
  normal: IComment[];
  highlighted: IComment[];
  max: number;
}

interface MyComment {
  exists: boolean;
  comment: IComment | null;
}

// used inf components/discuss/comment/comments
// list of comments in thought context
export default function useComments(
  topicid: number,
  thought: IThought,
  loadAll = false,
  initCommentsFromThought = false,
  allowMultipleComments = false
) {
  const isInitialized = ref(false);
  const loadingAllComments = ref(false);
  const showTeaser = ref(true);
  const isRefreshing = ref(false);
  const comments = reactive({
    normal: [],
    highlighted: [],
    max: 0,
  }) as CommentLists;

  // const comments = reactive([]);
  // const commentsHighlighted = reactive([]);
  // const maxComments = ref(0);

  const myComment = reactive({
    exists: false,
    comment: null,
  }) as MyComment;

  const showNormalComments = computed(() => {
    if (showTeaser.value) {
      // return false if either myComment or highlighted comment available as teaser
      return !(myComment.exists || comments.highlighted.length > 0);
    }
    return true;
  });

  const moreCommentsToLoad = computed(() => {
    let length = 0;
    if (myComment.exists && myComment.comment?.highlight == 0) {
      length = length + 1;
    }
    if (showTeaser.value) {
      return comments.max > 1 && length < comments.max && !loadingAllComments.value;
    }
    if (!showNormalComments.value && comments.max > 0) {
      return true;
    }
    length = length + comments.normal.length;
    return comments.max > 1 && length < comments.max && !loadingAllComments.value;
  });

  function getHighlightedComments(params?: any) {
    CommentRepository.getHighlighted(thought.id, params)
      .then((response: any) => {
        if (response.data) {
          //Initilize highlited comments so it doesnt Spawn multiple in list Wrapper
          comments.highlighted = [];
          response.data.forEach((comment: IComment) => {
            if (comment.user && comment.user.id == userStore.getState().user?.id && !allowMultipleComments) {
              myComment.exists = true;
              myComment.comment = comment;
            } else {
              let comment_inserted = false;
              for (let i = 0; i < comments.highlighted.length; i++) {
                if (comment.commentid == comments.highlighted[i].commentid) {
                  comments.highlighted.splice(i, 1, comment);
                  comment_inserted = true;
                  break;
                }
              }
              if (!comment_inserted) {
                comments.highlighted.push(comment);
              }
            }
          });
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  function getMyComment(callback?: () => any) {
    const userid = userStore.getState().user?.id;
    // don't bother calling API when no user is logged in
    if (!userid) {
      if (callback) {
        callback();
      }
      return;
    }

    CommentRepository.get(thought.id, { userid: userid })
      .then((response: any) => {
        if (response.data) {
          response.data.forEach((comment: IComment) => {
            if (comment.user && comment.user.id == userStore.getState().user?.id) {
              myComment.exists = true;
              myComment.comment = comment;
            }
          });
        }
        if (callback) {
          callback();
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  function getFirstComment(callback?: () => any) {
    const showMultipleComments = toBool(allowMultipleComments);
    CommentRepository.getFirst(thought.id, showMultipleComments)
      .then((response: any) => {
        if (response.data) {
          if (showMultipleComments) {
            // If showMultipleComments is true, the retrieved comments are only the highlighted ones
            comments.highlighted = [];
            comments.highlighted.push(...response.data);
          } else {
            comments.normal = [];
            response.data.forEach((comment: IComment) => {
              if (comment.user && comment.user.id == userStore.getState().user?.id && !allowMultipleComments) {
                myComment.exists = true;
                myComment.comment = comment;
              } else {
                let comment_inserted = false;
                for (let i = 0; i < comments.normal.length; i++) {
                  if (comment.commentid == comments.normal[i].commentid) {
                    comments.normal.splice(i, 1, comment);
                    comment_inserted = true;
                    break;
                  }
                }
                if (!comment_inserted) {
                  comments.normal.push(comment);
                }
              }
            });
          }
          comments.max = response.pager.total_entries;
        }
        if (callback) {
          callback();
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  function getAllComments(callback?: () => any) {
    loadingAllComments.value = true;
    CommentRepository.getRows(thought.id, comments.max)
      .then((response: any) => {
        if (response.data) {
          comments.normal = [];
          comments.max = response.pager.total_entries;
          response.data.forEach((comment: IComment) => {
            if (comment.user && comment.user.id == userStore.getState().user?.id && !allowMultipleComments) {
              myComment.exists = true;
              myComment.comment = comment;
            } else {
              let comment_inserted = false;
              for (let i = 0; i < comments.normal.length; i++) {
                if (comment.commentid == comments.normal[i].commentid) {
                  comments.normal.splice(i, 1, comment);
                  comment_inserted = true;
                  break;
                }
              }
              if (!comment_inserted) {
                comments.normal.push(comment);
              }
            }
          });
        }
        loadingAllComments.value = false;
        if (callback) {
          callback();
        }
      })
      .catch((error: any) => {
        loadingAllComments.value = false;
        console.log(error);
      });
  }

  function loadAllComments() {
    showTeaser.value = false;
    getAllComments(() => {
      // console.log('fetched all comments');
    });
  }

  function refreshComments(loadAll = true) {
    if (!isRefreshing.value) {
      isRefreshing.value = true;
      showTeaser.value = true;
      comments.normal = [];
      comments.highlighted = [];

      // try to get highlighted comment (if it exists)
      getHighlightedComments();

      // get a first comment from api, to get max available comments info
      getFirstComment(() => {
        if (!myComment.exists && !toBool(allowMultipleComments)) {
          // also try getting myComment if logged in user
          getMyComment(() => {
            isInitialized.value = true;
          });
        } else {
          isInitialized.value = true;
        }

        if (loadAll) {
          loadAllComments();
        }
      });
      isRefreshing.value = false;
    }
  }

  // use this to only refresh my comment when user changes on watch? TODO RFA UNTESTED
  function refreshMyComment(newUser: IUser) {
    if (!isRefreshing.value) {
      isRefreshing.value = true;
      showTeaser.value = true;

      // get a first comment from api, to get max available comments info
      if (!allowMultipleComments) {
        if (myComment.exists && myComment.comment && myComment.comment?.user.id !== newUser.id) {
          if (myComment.comment?.highlight) {
            comments.highlighted.push(myComment.comment);
          } else {
            comments.normal.push(myComment.comment);
          }
          myComment.comment = null;
          myComment.exists = false;
        }

        getMyComment(() => {
          isInitialized.value = true;

          if (myComment.exists) {
            const my_comment = myComment.comment;

            // find in comments normal and remove if found
            for (let i = 0; i < comments.normal.length; i++) {
              if (my_comment?.commentid == comments.normal[i].commentid) {
                comments.normal.splice(i, 1);
                break;
              }
            }
            // find in comments highlighted and remove if found
            for (let i = 0; i < comments.highlighted.length; i++) {
              if (my_comment?.commentid == comments.highlighted[i].commentid) {
                comments.highlighted.splice(i, 1);
                break;
              }
            }
          }
        });
      }
      isRefreshing.value = false;
    }
  }

  function getCommentsFromThought() {
    const data = thought.first_comments;
    comments.max = thought.commentcount;
    if (data.highlighted) {
      comments.highlighted = [];
      data.highlighted.forEach((comment: IComment) => {
        if (comment.user && comment.user.id == userStore.getState().user?.id) {
          myComment.exists = true;
          myComment.comment = comment;
        } else {
          comments.highlighted.push(comment);
        }
      });
    }
    if (data.first_normal) {
      comments.normal = [];
      comments.normal.push(data.first_normal);
    }
    if (data.my) {
      if (allowMultipleComments) {
        // if multiple-comments allowed, treat "my" comment as normal
        comments.normal.push(data.my);
      } else {
        myComment.exists = true;
        myComment.comment = data.my;
      }
      if (data.my.status == 'hide') {
        comments.max += 1;
      }
    }
    isInitialized.value = true;
  }

  watch(
    () => userStore.getState().user,
    (newUser, oldUser) => {
      if ((newUser == null && oldUser) || (newUser && oldUser == null) || newUser?.id != oldUser?.id) {
        if (!userStore.getState().user) {
          myComment.exists = false;
          myComment.comment = null;
        }
        refreshComments(loadAll);
      }
    }
  );

  // initial setup for comments
  // check if highlighted or "my" comment are available to display as teaser
  // if not show any normal comment
  onMounted(() => {
    if (!initCommentsFromThought) {
      refreshComments(loadAll);
    } else {
      getCommentsFromThought();
    }
  });

  return {
    isInitialized,
    isRefreshing,
    myComment,
    comments,
    loadingAllComments,
    showNormalComments,
    moreCommentsToLoad,
    loadAllComments,
    refreshComments,
  };
}
